import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ShieldCheck, AlertTriangle, Phone, UserPlus } from "lucide-react";
import {
  useWoContract,
  useWoTimeline,
  useWoTransition,
  useWorkOrder,
  useBranchEngineers,
  useAssignEngineer,
} from "@/features/work-orders/api";

import { SeverityBadge, WoStatusBadge, ClosedDueToDisposalBadge } from "@/features/work-orders/badges";
import type { WoStatus } from "@/features/work-orders/types";
import { useConfigWorkflows, useConfigStatuses } from "@/features/configuration/api";
import { formatDuration, useLiveMinutes, timeAgo } from "@/features/work-orders/format";
import { WoPhotoGallery, type WoPhoto } from "@/features/work-orders/PhotoUploader";
import { formatDate, fmtDateTime } from "@/features/assets/format";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { usePmPlanById, usePmExecutionByWoId } from "@/features/pm/api";
import { isHodOf } from "@/lib/roles";
import { stripPhone, validatePhone } from "@/lib/phone-validation";
import { SourceTicketCard } from "@/features/tickets/SourceTicketCard";
import { CompleteWorkOrderDialog } from "@/features/work-orders/CompleteWorkOrderDialog";
import { CancelWorkOrderDialog } from "@/features/work-orders/CancelWorkOrderDialog";
import { WorkOrderSparesCard } from "@/features/work-orders/WorkOrderSparesCard";
import { toCurrentUser, canCompleteWorkOrder, canCancelWorkOrder } from "@/lib/permissions";

import { useQueryClient, useQuery } from "@tanstack/react-query";
import { apiGet } from "@/integrations/api/client";
import { toast } from "sonner";
import { rpcErrorMessage } from "@/lib/rpc-error";

type TransitionPayloadDebug = {
  work_order_id: string;
  from_status: string;
  to_status: WoStatus;
  action: string;
  comment: string | null;
  rpc: "transition_work_order_status";
  rpc_params: {
    p_work_order_id: string;
    p_target_status: WoStatus;
    p_remarks: string | null;
  };
};

type TransitionDebugInfo = {
  woId: string;
  rawStatus: string;
  normalizedStatus: string;
  currentUserId: string | null;
  currentProfileId: string | null;
  assignedEngineerId: string | null;
  assignedUserId: string | null;
  roles: string[];
  isAssignee: boolean;
  canEngineer: boolean;
};

const TRANSITION_LABELS: Record<string, string> = {
  "Reported→Acknowledged": "Acknowledge",
  "Acknowledged→In Progress": "Start Work",
  "In Progress→Resolved": "Mark Resolved",
  "In Progress→Awaiting Spares": "Awaiting Spares",
  "In Progress→Awaiting Vendor": "Awaiting Vendor",
  "Awaiting Spares→In Progress": "Resume Work",
  "Awaiting Vendor→In Progress": "Resume Work",
  "Awaiting Spares→Resolved": "Mark Resolved",
  "Awaiting Vendor→Resolved": "Mark Resolved",
  "Resolved→Verified & Closed": "Verify & Close",
};

function transitionLabel(from: string, to: string): string {
  return TRANSITION_LABELS[`${from}→${to}`] ?? `Status → ${to}`;
}

export const Route = createFileRoute("/_authenticated/work-orders/$woId/")({
  component: WoDetailPage,
  errorComponent: WoErrorFallback,
});

function WoErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <Card className="mx-auto max-w-lg">
      <CardContent className="space-y-3 p-6 text-center">
        <AlertTriangle className="mx-auto h-8 w-8 text-destructive" />
        <h2 className="text-lg font-semibold">This work order couldn't be loaded</h2>
        <p className="text-sm text-muted-foreground">{error?.message ?? "Unexpected error."}</p>
        <Button onClick={reset}>Try again</Button>
      </CardContent>
    </Card>
  );
}

function WoDetailPage() {
  const { woId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: wo, isLoading, isError } = useWorkOrder(woId);
  const { data: timeline = [] } = useWoTimeline(woId);
  const { data: me, isLoading: meLoading } = useCurrentUser();
  const { data: contract } = useWoContract(wo?.contract_id ?? null, wo?.asset_id);
  const pmExecQ = usePmExecutionByWoId(woId);
  const transition = useWoTransition();
  const [completeOpen, setCompleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState<WoStatus | null>(null);
  const [lastTransitionPayload, setLastTransitionPayload] = useState<TransitionPayloadDebug | null>(null);
  const [lastTransitionError, setLastTransitionError] = useState<string | null>(null);
  const [generatedActionButtons, setGeneratedActionButtons] = useState<string[]>([]);

  const isClosedOrResolved = ["Resolved", "Verified & Closed", "Cancelled"].includes(wo?.status || "");
  const effDowntimeEnd = wo?.downtime_end ?? (isClosedOrResolved ? (wo?.closed_at ?? wo?.resolved_at) : null);
  const live = useLiveMinutes(wo?.downtime_start ?? wo?.reported_at ?? null, effDowntimeEnd);
  if (isLoading || meLoading) return <DetailSkeleton />;
  if (isError || !wo) {
    return (
      <Card className="mx-auto max-w-lg">
        <CardContent className="space-y-3 p-6 text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-muted-foreground" />
          <h2 className="text-lg font-semibold">You do not have access to this record.</h2>
          <p className="text-sm text-muted-foreground">
            This work order belongs to another department or has been removed.
          </p>
          <Button onClick={() => navigate({ to: "/work-orders" })}>Back to Work Orders</Button>
        </CardContent>
      </Card>
    );
  }

  const liveMinutes = wo.downtime_minutes ?? live;
  const roles = me?.roles ?? [];
  const uid = me?.user.id;
  const isSuperAdmin = roles.includes("super_admin");
  const isBiomedHead = roles.includes("biomed_head");
  const isBiomedEngineer = roles.includes("biomed_engineer");
  const isAdmin = isSuperAdmin || isBiomedHead;
  const isFacility = roles.includes("facility_manager");
  const isOverride = isAdmin || isFacility;
  const isAssignee = !!wo.assigned_to && wo.assigned_to === uid;
  const canEngineer = isAssignee || isOverride;
  const isReporter = !!uid && uid === wo.reported_by;
  const reportingDeptId =
    (wo as { reporting_department_id?: string | null }).reporting_department_id ?? wo.department_id ?? null;
  const isReportingDeptUser = !!(
    roles.includes("dept_user") &&
    me?.profile?.department_id &&
    me.profile.department_id === reportingDeptId
  );
  const isHodOfReportingDept = isHodOf(me, reportingDeptId);
  const canVerifyNormal = isReporter || isReportingDeptUser || isHodOfReportingDept;
  const canVerify = canVerifyNormal || isSuperAdmin;
  const isSuperAdminOverride = isSuperAdmin && !canVerifyNormal;
  const isHodOfAssetDept = isHodOf(me, wo.asset?.custodian_dept_id ?? null);
  const isAssetDisposed = wo.asset?.status === "Disposed";
  const isAssetCondemned = wo.asset?.status === "Condemned";
  const closedDueToDisposal = isAssetDisposed || wo.closure_remark === "Closed due to asset disposal";
  const isCancelled = (wo.status as string) === "Cancelled";
  const isClosed = wo.status === "Verified & Closed" || closedDueToDisposal || isCancelled;
  const hasSourceTicket = !!(wo as { source_ticket_id?: string | null }).source_ticket_id;
  const cu = me ? toCurrentUser(me) : null;
  const canComplete = !!cu && canCompleteWorkOrder(cu, wo);
  const canCancel = !!cu && canCancelWorkOrder(cu, wo);
  const sourceTicketAssignedUserId =
    (wo.source_ticket as { assigned_user_id?: string | null } | null | undefined)?.assigned_user_id ?? null;
  const isPmExecutionCompleted = wo.type !== "PM" || pmExecQ.data?.status === "Completed";
  const transitionDebugInfo: TransitionDebugInfo = {
    woId: wo.id,
    rawStatus: String(wo.status),
    normalizedStatus: String(wo.status).trim(),
    currentUserId: uid ?? null,
    currentProfileId: me?.profile?.id ?? null,
    assignedEngineerId: wo.assigned_to ?? null,
    assignedUserId: sourceTicketAssignedUserId,
    roles,
    isAssignee,
    canEngineer,
  };

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/work-orders" })}>
        <ArrowLeft className="mr-1 h-4 w-4" />
        Back to Work Orders
      </Button>

      {/* 1. Top Header Card */}
      <Card>
        <CardContent className="flex flex-wrap items-start justify-between gap-3 p-5">
          <div className="space-y-1">
            <Badge variant="outline" className="font-mono">
              {wo.wo_number}
            </Badge>
            <h1 className="text-2xl font-semibold tracking-tight">
              {wo.asset ? (
                <Link to="/assets/$assetId" params={{ assetId: wo.asset.id }} className="hover:underline">
                  {wo.asset.name} <span className="text-muted-foreground ml-1">[{wo.asset.asset_code}]</span>
                </Link>
              ) : (
                <span className="text-muted-foreground">Asset details unavailable</span>
              )}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Badge variant="default" className="bg-primary/10 text-primary hover:bg-primary/20">
                {wo.type}
              </Badge>
              <SeverityBadge value={wo.severity} />
              <WoStatusBadge value={wo.status} />
              
              {closedDueToDisposal && <ClosedDueToDisposalBadge />}
              {isAssetDisposed && (
                <Badge variant="outline" className="border-muted-foreground/40 text-muted-foreground">
                  Asset Disposed
                </Badge>
              )}
              {isAssetCondemned && (
                <Badge variant="outline" className="border-destructive/40 text-destructive">
                  Asset Condemned
                </Badge>
              )}
              {wo.is_under_contract && (
                <Badge variant="secondary">
                  <ShieldCheck className="mr-1 h-3 w-3" />
                  Under contract
                </Badge>
              )}
              {wo.assignee ? (
                <Badge variant="outline">Assigned: {wo.assignee.full_name || wo.assignee.email || "—"}</Badge>
              ) : (
                <Badge variant="outline" className="border-amber-500/50 text-amber-700 dark:text-amber-300">
                  Unassigned
                </Badge>
              )}
              {wo.type === "PM" && wo.pm_plan_id && (
                <span className="text-xs text-muted-foreground ml-1">(PM Plan linked)</span>
              )}
              <span className="text-sm text-muted-foreground ml-auto">Reported {timeAgo(wo.reported_at)}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase text-muted-foreground">Downtime</div>
            <div className="text-2xl font-semibold tabular-nums">{formatDuration(liveMinutes ?? null)}</div>
          </div>
        </CardContent>
      </Card>

      {/* 2. Source Ticket Card */}
      {hasSourceTicket && <SourceTicketCard woId={wo.id} />}

      {/* 3. Status Action Bar */}
      {!isClosed && (
        <Actions
          wo={wo as any}
          roles={roles}
          canEngineer={canEngineer}
          canVerify={canVerify && !hasSourceTicket}
          isHodOfAssetDept={isHodOfAssetDept}
          isSuperAdminOverride={isSuperAdminOverride && !hasSourceTicket}
          isBiomedHead={isBiomedHead}
          isBiomedEngineer={isBiomedEngineer}
          suppressMarkResolved={canComplete && isPmExecutionCompleted}
          debugInfo={transitionDebugInfo}
          isTransitionPending={transition.isPending}
          pendingTarget={transitionTarget}
          inlineError={lastTransitionError}
          onTargetsChange={setGeneratedActionButtons}
          onTransition={async (to, payload) => {
            const remarks =
              (payload?.remarks as string | null | undefined) ??
              (payload?.closure_remark as string | null | undefined) ??
              null;
            const action = transitionLabel(String(wo.status), to);
            const transitionPayload: TransitionPayloadDebug = {
              work_order_id: wo.id,
              from_status: String(wo.status),
              to_status: to,
              action,
              comment: remarks,
              rpc: "transition_work_order_status",
              rpc_params: {
                p_work_order_id: wo.id,
                p_target_status: to,
                p_remarks: remarks,
              },
            };
            setTransitionTarget(to);
            setLastTransitionPayload(transitionPayload);
            setLastTransitionError(null);
            if (import.meta.env.DEV) console.info("[wo-transition-payload]", transitionPayload);
            try {
              await transition.mutateAsync({ wo_id: wo.id, to, payload });
              await Promise.all([
                queryClient.refetchQueries({ queryKey: ["work_order", wo.id] }),
                queryClient.refetchQueries({ queryKey: ["wo_timeline", wo.id] }),
              ]);
              if (wo.source_ticket_id) {
                queryClient.invalidateQueries({ queryKey: ["ticket", wo.source_ticket_id] });
                queryClient.invalidateQueries({ queryKey: ["ticket-linked-wo"] });
                queryClient.invalidateQueries({ queryKey: ["tickets"] });
              }
              toast.success(to === "Acknowledged" ? "Work Order acknowledged." : `Status → ${to}`);
            } catch (e) {
              const message = rpcErrorMessage(e);
              console.error("[wo-transition]", e);
              setLastTransitionError(message);
              toast.error(
                to === "Acknowledged"
                  ? `Unable to acknowledge Work Order: ${message}`
                  : `Unable to update Work Order: ${message}`,
              );
            } finally {
              setTransitionTarget(null);
            }
          }}
        />
      )}

      {/* 4. Assign Engineer Card */}
      {!wo.assigned_to && isAdmin && !isClosed && (
        <AssignEngineerCard woId={wo.id} branchId={wo.branch_id ?? wo.asset?.branch_id ?? null} />
      )}

      {/* 5. Contract Coverage Card */}
      {(wo.is_under_contract || wo.contract_id) && contract && (
        <ContractCoverageCard
          contract={contract as ContractCoverageData}
          reportedAt={wo.reported_at}
          acknowledgedAt={wo.acknowledged_at}
          resolvedAt={wo.resolved_at}
        />
      )}

      {/* Complete / Cancel buttons */}
      {(canComplete || canCancel) && (
        <div className="flex flex-wrap items-center gap-2">
          {canComplete && (
            <Button disabled={!isPmExecutionCompleted} onClick={() => setCompleteOpen(true)}>
              Complete Work Order
            </Button>
          )}
          {canCancel && (
            <Button
              variant="outline"
              className="border-destructive/40 text-destructive hover:bg-destructive/10"
              onClick={() => setCancelOpen(true)}
            >
              Cancel Work Order
            </Button>
          )}
          {hasSourceTicket && wo.status === "Resolved" && (
            <span className="text-xs text-muted-foreground">Awaiting verification on the source ticket.</span>
          )}
        </div>
      )}

      <CompleteWorkOrderDialog
        open={completeOpen}
        onOpenChange={setCompleteOpen}
        woId={wo.id}
        woNumber={wo.wo_number}
      />
      <CancelWorkOrderDialog open={cancelOpen} onOpenChange={setCancelOpen} woId={wo.id} woNumber={wo.wo_number} />

      {/* TWO-COLUMN BODY */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 mt-4">
        
        {/* LEFT COLUMN: Work Content */}
        <div className="space-y-4 lg:col-span-2">
          
          {/* 10. Checklist / PM Execution Card (Moved to top) */}
          {wo.type === "PM" && (canEngineer || isAdmin || isFacility) && (
            <PmCard
              wo={wo as any}
              onOpen={() => {
                try {
                  navigate({ to: "/work-orders/$woId/pm-execution", params: { woId: wo.id } });
                } catch (e) {
                  console.error("PM Execution navigation failed", e);
                  toast.error("Could not open PM Execution page.");
                }
              }}
            />
          )}

          {/* 6. Details Card */}
          <DetailsCard
            wo={wo as any}
            canEdit={canEngineer && wo.status === "In Progress"}
            onSave={async (payload) => {
              const { getSupabase } = await import("@/integrations/external-supabase/client");
              const { error } = await getSupabase().from("work_orders").update(payload).eq("id", wo.id);
              if (error) {
                toast.error(error.message);
                return;
              }
              toast.success("Saved");
              queryClient.invalidateQueries({ queryKey: ["work_order", wo.id] });
            }}
          />

          {/* 7. Vendor / Service Details Card */}
          {(wo.is_under_contract || wo.contract_id || wo.vendor_id) && (
            <VendorServiceDetailsCard
              wo={wo as any}
              canEdit={(isAdmin || isFacility || roles.includes("biomed_engineer")) && !isClosed}
            />
          )}

          {/* 8. Cost Summary Card */}
          {isClosed && <CostSummaryCard wo={wo as any} />}

          {/* 9. Spare Requests / Consumption Card */}
          <div className="space-y-2">
            <WorkOrderSparesCard wo={wo as any} />
            {hasSourceTicket && (
              <div className="text-xs text-amber-600 px-1 font-medium">
                Note: Spare requests are shared with the source ticket.
              </div>
            )}
          </div>


          {/* 11. Photos & Documents Card */}
          <PhotosCard woId={wo.id} photos={(wo.photos ?? []) as WoPhoto[]} canEdit={canEngineer && !isClosed} />

          {/* 12. Completion Summary Card */}
          {wo.status === "Resolved" || isClosed ? (
            (wo.completion_summary || wo.asset_running_status || wo.completed_at) && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Completion Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-[10px] uppercase text-muted-foreground">Completed At</div>
                      <div>{wo.completed_at ? formatDate(wo.completed_at) : "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-muted-foreground">Asset Running Status</div>
                      <div>{wo.asset_running_status ?? "—"}</div>
                    </div>
                  </div>
                  {wo.completion_summary && (
                    <div>
                      <div className="text-[10px] uppercase text-muted-foreground">Work Done Summary</div>
                      <div className="whitespace-pre-wrap rounded border bg-muted/30 p-2 text-xs">
                        {wo.completion_summary}
                      </div>
                    </div>
                  )}
                  {wo.completion_notes && (
                    <div>
                      <div className="text-[10px] uppercase text-muted-foreground">Notes</div>
                      <div className="whitespace-pre-wrap text-xs">{wo.completion_notes}</div>
                    </div>
                  )}
                  {wo.service_report_url && (
                    <div>
                      <div className="text-[10px] uppercase text-muted-foreground">Service Report</div>
                      <a
                        href={wo.service_report_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline text-xs"
                      >
                        Open report
                      </a>
                    </div>
                  )}
                  {wo.engineer_recommendation && (
                    <div>
                      <div className="text-[10px] uppercase text-muted-foreground">Engineer Recommendation</div>
                      <div className="whitespace-pre-wrap text-xs">{wo.engineer_recommendation}</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          ) : null}

          {/* 13. Cancellation Card */}
          {isCancelled && (
            <Card className="border-destructive/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Cancellation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Cancelled At</div>
                    <div>{wo.cancelled_at ? formatDate(wo.cancelled_at) : "—"}</div>
                  </div>
                </div>
                {wo.cancellation_reason && (
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Reason</div>
                    <div className="whitespace-pre-wrap rounded border bg-muted/30 p-2 text-xs">
                      {wo.cancellation_reason}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

        </div>

        {/* RIGHT COLUMN: Timeline & Related */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {timeline.length === 0 && <p className="text-sm text-muted-foreground">No status events recorded yet.</p>}
              {(
                timeline as unknown as Array<{
                  id: string;
                  from_status: WoStatus | null;
                  to_status: WoStatus;
                  note: string | null;
                  created_at: string;
                  actor: { full_name: string | null; email: string | null } | null;
                }>
              ).map((e) => (
                <div key={e.id} className="relative pl-5">
                  <span className="absolute left-0 top-1.5 h-2 w-2 rounded-full bg-primary" />
                  <div className="text-sm font-medium">{e.to_status}</div>
                  <div className="text-xs text-muted-foreground">
                    {fmtDateTime(e.created_at)} · {e.actor?.full_name || e.actor?.email || "system"}
                  </div>
                  {e.note && <div className="mt-1 text-xs">{e.note}</div>}
                </div>
              ))}
            </CardContent>
          </Card>
          
          <RelatedRecordsCard assetId={wo.asset?.id} woId={wo.id} />
        </div>
      </div>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-40" />
      <Card>
        <CardContent className="space-y-3 p-5">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-5 w-80" />
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  );
}

function AssignEngineerCard({ woId, branchId }: { woId: string; branchId: string | null }) {
  const { data: engineers = [] } = useBranchEngineers(branchId);
  const assign = useAssignEngineer();
  const [val, setVal] = useState<string>("");
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-2 p-3">
        <UserPlus className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Assign engineer:</span>
        <Select value={val} onValueChange={setVal}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Select engineer…" />
          </SelectTrigger>
          <SelectContent>
            {engineers.length === 0 && (
              <div className="p-2 text-xs text-muted-foreground">No engineers in this branch</div>
            )}
            {engineers.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                {e.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          disabled={!val || assign.isPending}
          onClick={async () => {
            try {
              await assign.mutateAsync({ wo_id: woId, engineer_id: val });
              toast.success("Engineer assigned");
            } catch (e) {
              toast.error((e as Error).message);
            }
          }}
        >
          Assign
        </Button>
      </CardContent>
    </Card>
  );
}

type ActionOption = {
  key: string;
  to: WoStatus;
  label: string;
  variant?: "default" | "outline";
};

function Actions({
  wo,
  roles,
  canEngineer,
  canVerify,
  isHodOfAssetDept,
  isSuperAdminOverride,
  isBiomedHead,
  isBiomedEngineer,
  suppressMarkResolved,
  debugInfo,
  isTransitionPending,
  pendingTarget,
  inlineError,
  onTargetsChange,
  onTransition,
}: {
  wo: { status: WoStatus; root_cause: string | null; action_taken: string | null };
  roles: string[];
  canEngineer: boolean;
  canVerify: boolean;
  isHodOfAssetDept?: boolean;
  isSuperAdminOverride?: boolean;
  isBiomedHead?: boolean;
  isBiomedEngineer?: boolean;
  suppressMarkResolved?: boolean;
  debugInfo: TransitionDebugInfo;
  isTransitionPending: boolean;
  pendingTarget: WoStatus | null;
  inlineError: string | null;
  onTargetsChange?: (targets: string[]) => void;
  onTransition: (to: WoStatus, payload?: Record<string, unknown>) => Promise<void>;
}) {
  const [closureRemark, setClosureRemark] = useState("");
  const { data: workflows } = useConfigWorkflows("work_orders");
  const { data: statuses } = useConfigStatuses("work_orders");
  const isSuperAdmin = roles.includes("super_admin");

  const statusByKey = new Map((statuses ?? []).map((s) => [s.status_key, s] as const));
  const currentStatus = statusByKey.get(wo.status as string);
  const isFinal =
    currentStatus?.is_final ||
    wo.status === "Verified & Closed" ||
    (wo.status as string) === "Cancelled" ||
    (wo.status as string) === "Closed";

  // Workflow-driven transitions (config-first, fallback to hardcoded ladder)
  const activeFlows = (workflows ?? []).filter(
    (w) =>
      w.is_active &&
      w.from_status_key === (wo.status as string) &&
      (isSuperAdmin || (w.allowed_roles ?? []).length === 0 || (w.allowed_roles ?? []).some((r) => roles.includes(r))),
  );

  const options: ActionOption[] = [];
  const pushed = new Set<string>();

  const pushOption = (key: string, to: WoStatus, label: string, variant: "default" | "outline" = "default") => {
    if (pushed.has(to)) return;
    pushed.add(to);
    options.push({ key, to, label, variant });
  };

  // Config-driven transitions
  for (const w of activeFlows) {
    if (suppressMarkResolved && w.to_status_key === "Resolved") continue;
    const target = statusByKey.get(w.to_status_key);
    const label =
      TRANSITION_LABELS[`${w.from_status_key}→${w.to_status_key}`] ?? target?.status_label ?? `→ ${w.to_status_key}`;
    pushOption(w.id, w.to_status_key as WoStatus, label, "default");
  }

  // Fallback ladder for the assigned engineer (or admin/facility override).
  // Ensures buttons appear even if config workflow role lists drift or the
  // workflows query has not settled yet.
  if (canEngineer) {
    if (wo.status === "Reported") pushOption("ack", "Acknowledged", "Acknowledge");
    if (wo.status === "Acknowledged") pushOption("start", "In Progress", "Start Work");
    if (wo.status === "In Progress") {
      pushOption("sp", "Awaiting Spares", "Awaiting Spares", "outline");
      pushOption("vn", "Awaiting Vendor", "Awaiting Vendor", "outline");
      if (!suppressMarkResolved) pushOption("re", "Resolved", "Mark Resolved");
    }
    if (wo.status === "Awaiting Spares" || wo.status === "Awaiting Vendor") {
      pushOption("resume", "In Progress", "Resume Work");
      if (!suppressMarkResolved) pushOption("re2", "Resolved", "Mark Resolved");
    }
  }

  const generatedTargets = isFinal ? [] : options.map((option) => `${option.label} (${wo.status} → ${option.to})`);

  useEffect(() => {
    onTargetsChange?.(generatedTargets);
  }, [generatedTargets.join("|"), onTargetsChange]);

  // The Resolved → Verified & Closed path keeps its specialised closure-remark card
  // for any user who can verify.
  if (wo.status === "Resolved" && canVerify) {
    const title = isSuperAdminOverride ? "Admin Override: Verify & Close" : "Verify & Close";
    const remarkRequired = !!isSuperAdminOverride;
    const disabled = remarkRequired && closureRemark.trim().length === 0;
    return (
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <Label>{title}</Label>
            {isSuperAdminOverride ? (
              <Badge className="bg-destructive/15 text-destructive border-destructive/30" variant="outline">
                Admin Override
              </Badge>
            ) : (
              isHodOfAssetDept && (
                <Badge
                  className="bg-amber-500/15 text-amber-700 border-amber-500/30 dark:text-amber-300"
                  variant="outline"
                >
                  HOD / In-charge
                </Badge>
              )
            )}
          </div>
          <Textarea
            value={closureRemark}
            onChange={(e) => setClosureRemark(e.target.value)}
            placeholder={remarkRequired ? "Remark required for admin override or rejection…" : "Confirm the issue is fixed, or explain why it is being rejected…"}
            rows={3}
          />
          <div className="flex gap-3">
            <Button
              className="flex-1"
              disabled={disabled}
              onClick={() => onTransition("Verified & Closed", { closure_remark: closureRemark })}
            >
              {title}
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              disabled={closureRemark.trim().length === 0}
              onClick={() => onTransition("In Progress", { closure_remark: closureRemark })}
            >
              Reject Resolution
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (wo.status === "Resolved" && (isBiomedHead || isBiomedEngineer)) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          Work order resolved. Awaiting verification and closure by the reporting department.
        </CardContent>
      </Card>
    );
  }

  if (isFinal) return null;

  const handleTransition = (option: ActionOption) => {
    if (option.to === "Acknowledged") {
      console.info("Acknowledge clicked", {
        work_order_id: debugInfo.woId,
        current_status_raw: debugInfo.rawStatus,
        target_status: option.to,
        current_user_id: debugInfo.currentUserId,
        current_profile_id: debugInfo.currentProfileId,
        assigned_engineer_id: debugInfo.assignedEngineerId,
        assigned_user_id: debugInfo.assignedUserId,
        roles: debugInfo.roles,
        isAssignee: debugInfo.isAssignee,
        canEngineer: debugInfo.canEngineer,
      });
    }
    void onTransition(option.to);
  };

  if (options.length === 0) {
    return (
      <Card>
        <CardContent className="p-3 text-xs text-muted-foreground">
          No transitions available for your role on this work order. Ask your supervisor to reassign or update workflow
          permissions.
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="space-y-2 p-3">
        <div className="flex flex-wrap gap-2">
          {options.map((option) => (
            <Button
              key={option.key}
              variant={option.variant ?? "default"}
              disabled={isTransitionPending}
              onClick={() => handleTransition(option)}
            >
              {isTransitionPending && pendingTarget === option.to ? "Updating…" : option.label}
            </Button>
          ))}
        </div>
        {inlineError && <p className="text-xs text-destructive">{inlineError}</p>}
      </CardContent>
    </Card>
  );
}

// function WorkOrderDebugPanel({ info, generatedButtons, lastPayload, lastError }: {
//   info: TransitionDebugInfo;
//   generatedButtons: string[];
//   lastPayload: TransitionPayloadDebug | null;
//   lastError: string | null;
// }) {
//   return (
//     <Card className="border-dashed">
//       <CardContent className="p-3">
//         <details className="text-xs">
//           <summary className="cursor-pointer font-medium text-muted-foreground">Work Order transition debug</summary>
//           <pre className="mt-3 max-h-80 overflow-auto rounded border bg-muted/30 p-3 text-[11px] leading-relaxed">
//             {JSON.stringify({ ...info, targetButtonsGenerated: generatedButtons, lastMutationPayload: lastPayload, lastMutationError: lastError }, null, 2)}
//           </pre>
//         </details>
//       </CardContent>
//     </Card>
//   );
// }

function DetailsCard({
  wo,
  canEdit,
  onSave,
}: {
  wo: {
    root_cause: string | null;
    action_taken: string | null;
    labour_hours: number | null;
    external_cost: number | null;
    external_invoice_ref: string | null;
    external_gst: number | null;
    problem_description: string;
    diagnosis_notes?: string | null;
    reporter?: { full_name: string | null; email: string | null } | null;
    reported_at: string;
    severity: string;
    asset?: { location_path?: string | null } | null;
  };
  canEdit: boolean;
  onSave: (payload: Record<string, unknown>) => Promise<void>;
}) {
  const [rootCause, setRootCause] = useState(wo.root_cause ?? "");
  const [actionTaken, setActionTaken] = useState(wo.action_taken ?? "");
  const [diagnosisNotes, setDiagnosisNotes] = useState(wo.diagnosis_notes ?? "");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        
        <div className="grid grid-cols-2 gap-3 text-sm border-b pb-3">
          <Field label="Reported By">
            <div>{wo.reporter?.full_name || wo.reporter?.email || "—"}</div>
            <div className="text-xs text-muted-foreground">{fmtDateTime(wo.reported_at)}</div>
          </Field>
          <Field label="Location">
            <div>{wo.asset?.location_path || "—"}</div>
          </Field>
          <Field label="Severity">
            <div>{wo.severity}</div>
          </Field>
        </div>

        <div>
          <Label className="text-xs text-muted-foreground">Problem Description</Label>
          <p className="text-sm whitespace-pre-wrap">{wo.problem_description}</p>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="md:col-span-2">
            <Field label="Diagnosis Notes">
              <Textarea 
                value={diagnosisNotes} 
                onChange={(e) => setDiagnosisNotes(e.target.value)} 
                disabled={!canEdit} 
                rows={2} 
                placeholder="Initial findings / diagnostics..."
              />
            </Field>
          </div>
          <Field label="Root Cause *">
            <Textarea value={rootCause} onChange={(e) => setRootCause(e.target.value)} disabled={!canEdit} rows={2} />
          </Field>
          <Field label="Action Taken *">
            <Textarea
              value={actionTaken}
              onChange={(e) => setActionTaken(e.target.value)}
              disabled={!canEdit}
              rows={2}
            />
          </Field>
        </div>
        {canEdit && (
          <Button
            onClick={() =>
              onSave({
                root_cause: rootCause || null,
                action_taken: actionTaken || null,
                diagnosis_notes: diagnosisNotes || null,
              })
            }
          >
            Save Details
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function CostSummaryCard({
  wo,
}: {
  wo: {
    labour_hours: number | null;
    external_cost: number | null;
    external_gst: number | null;
    spares_cost: number | null;
  };
}) {
  const labour = Number(wo.labour_hours ?? 0);
  const spareCost = Number(wo.spares_cost ?? 0);
  const vendorCost = Number(wo.external_cost ?? 0) + Number(wo.external_gst ?? 0);
  const totalCost = spareCost + vendorCost;
  const fmt = (n: number) => `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Cost Summary</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div>
            <div className="text-xs text-muted-foreground">Labour</div>
            <div className="text-lg font-semibold tabular-nums">{labour ? `${labour} hrs` : "—"}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Spare Cost</div>
            <div className="text-lg font-semibold tabular-nums">{fmt(spareCost)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Vendor Cost</div>
            <div className="text-lg font-semibold tabular-nums">{fmt(vendorCost)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Total</div>
            <div className="text-lg font-semibold tabular-nums">{fmt(totalCost)}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PhotosCard({ woId, photos, canEdit }: { woId: string; photos: WoPhoto[]; canEdit: boolean }) {
  const qc = useQueryClient();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Photos</CardTitle>
      </CardHeader>
      <CardContent>
        {photos.length === 0 && !canEdit && <p className="text-sm text-muted-foreground">No photos uploaded.</p>}
        <WoPhotoGallery
          woId={woId}
          photos={photos}
          canEdit={canEdit}
          onChange={() => qc.invalidateQueries({ queryKey: ["work_order", woId] })}
        />
      </CardContent>
    </Card>
  );
}

function PmCard({
  wo,
  onOpen,
}: {
  wo: { id: string; status: WoStatus; pm_plan_id: string | null };
  onOpen: () => void;
}) {
  const planQ = usePmPlanById(wo.pm_plan_id ?? null);
  const isOpenable = wo.status === "In Progress";
  return (
    <Card>
      <CardContent className="flex items-center justify-between gap-3 p-3">
        <div className="text-sm">
          <div>This is a Preventive Maintenance work order.</div>
          <div className="text-xs text-muted-foreground">
            Checklist Template:{" "}
            <span className="font-medium">
              {!wo.pm_plan_id
                ? "No plan linked"
                : planQ.isLoading
                  ? "Loading"
                  : (planQ.data?.template?.name ?? "Not configured")}
            </span>
          </div>
        </div>
        <Button onClick={onOpen}>{isOpenable ? "Open PM Execution" : "View PM Execution"}</Button>
      </CardContent>
    </Card>
  );
}

type ContractCoverageData = {
  id?: string;
  contract_no?: string | null;
  type?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  status?: string | null;
  response_sla_hours?: number | null;
  resolution_sla_hours?: number | null;
  uptime_guarantee_pct?: number | null;
  penalty_clause?: string | null;
  spares_included?: boolean | null;
  coverage_scope?: string | null;
  l1_name?: string | null;
  l1_phone?: string | null;
  vendor?: { name?: string | null } | null;
};

function dash(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

function sparesCoverageLabel(c: ContractCoverageData): string {
  const t = (c.type ?? "").toUpperCase();
  if (t === "CMC") return "Included (CMC)";
  if (t === "AMC") return "Excluded (AMC)";
  if (c.spares_included === true) return "Included";
  if (c.spares_included === false) return "Excluded";
  return c.coverage_scope ?? "—";
}

function ContractCoverageCard({
  contract,
  reportedAt,
  acknowledgedAt,
  resolvedAt,
}: {
  contract: ContractCoverageData;
  reportedAt: string | null;
  acknowledgedAt: string | null;
  resolvedAt: string | null;
}) {
  const respSla = contract.response_sla_hours ?? null;
  const resoSla = contract.resolution_sla_hours ?? null;
  const noSla = respSla == null && resoSla == null;
  const resp = computeSlaState(reportedAt, acknowledgedAt, respSla);
  const reso = computeSlaState(reportedAt, resolvedAt, resoSla);
  const anyBreached = resp.state === "breached" || reso.state === "breached";
  const headerTone = anyBreached ? "border-destructive/40" : "border-emerald-500/30";

  return (
    <Card className={headerTone}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          {anyBreached ? (
            <AlertTriangle className="h-4 w-4 text-destructive" />
          ) : (
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
          )}
          Contract Coverage
        </CardTitle>
        {contract.l1_phone && (
          <a className="text-xs underline inline-flex items-center gap-1" href={`tel:${contract.l1_phone}`}>
            <Phone className="h-3 w-3" />
            {contract.l1_name ? `${contract.l1_name} · ` : ""}
            {contract.l1_phone}
          </a>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 text-sm">
          <Field label="Contract Type">
            <div>{dash(contract.type)}</div>
          </Field>
          <Field label="Vendor">
            <div>{dash(contract.vendor?.name)}</div>
          </Field>
          <Field label="Contract Number">
            <div className="font-mono text-xs">{dash(contract.contract_no)}</div>
          </Field>
          <Field label="Start Date">
            <div>{contract.start_date ? formatDate(contract.start_date) : "—"}</div>
          </Field>
          <Field label="End Date">
            <div>{contract.end_date ? formatDate(contract.end_date) : "—"}</div>
          </Field>
          <Field label="Contract Status">
            <div>{dash(contract.status)}</div>
          </Field>
          <Field label="Response SLA (hrs)">
            <div>{respSla != null ? `${respSla}h` : "—"}</div>
          </Field>
          <Field label="Resolution SLA (hrs)">
            <div>{resoSla != null ? `${resoSla}h` : "—"}</div>
          </Field>
          <Field label="Uptime Guarantee">
            <div>{contract.uptime_guarantee_pct != null ? `${contract.uptime_guarantee_pct}%` : "—"}</div>
          </Field>
          <Field label="Spares Coverage">
            <div>{sparesCoverageLabel(contract)}</div>
          </Field>
          <Field label="Breakdown Calls">
            <div>{dash(contract.coverage_scope)}</div>
          </Field>
          <Field label="Penalty Clause">
            <div className="text-xs">{dash(contract.penalty_clause)}</div>
          </Field>
        </div>
        {noSla ? (
          <div className="text-sm text-muted-foreground">SLA not configured for this contract.</div>
        ) : (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <SlaPill label="Response SLA" state={resp.state} detail={resp.detail} />
            <SlaPill label="Resolution SLA" state={reso.state} detail={reso.detail} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function VendorServiceDetailsCard({
  wo,
  canEdit,
}: {
  wo: {
    id: string;
    vendor_call_id: string | null;
    vendor_contacted_at: string | null;
    vendor_engineer_name: string | null;
    vendor_engineer_phone: string | null;
    vendor_visit_at: string | null;
    vendor_visit_date: string | null;
    vendor_service_report_url: string | null;
    vendor_remarks: string | null;
  };
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const toLocal = (v: string | null) => (v ? new Date(v).toISOString().slice(0, 16) : "");
  const initialVisit =
    wo.vendor_visit_at ?? (wo.vendor_visit_date ? new Date(wo.vendor_visit_date).toISOString() : null);
  const [callId, setCallId] = useState(wo.vendor_call_id ?? "");
  const [contactedAt, setContactedAt] = useState(toLocal(wo.vendor_contacted_at));
  const [engName, setEngName] = useState(wo.vendor_engineer_name ?? "");
  const [engPhone, setEngPhone] = useState(wo.vendor_engineer_phone ?? "");
  const [visitAt, setVisitAt] = useState(toLocal(initialVisit));
  const [reportUrl, setReportUrl] = useState(wo.vendor_service_report_url ?? "");
  const [remarks, setRemarks] = useState(wo.vendor_remarks ?? "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const phoneErr = validatePhone(engPhone);
    if (phoneErr) {
      toast.error(phoneErr);
      return;
    }
    setSaving(true);
    try {
      const { getSupabase } = await import("@/integrations/external-supabase/client");
      const payload: Record<string, unknown> = {
        vendor_call_id: callId || null,
        vendor_contacted_at: contactedAt ? new Date(contactedAt).toISOString() : null,
        vendor_engineer_name: engName || null,
        vendor_engineer_phone: engPhone || null,
        vendor_visit_at: visitAt ? new Date(visitAt).toISOString() : null,
        vendor_visit_date: visitAt ? new Date(visitAt).toISOString().slice(0, 10) : null,
        vendor_service_report_url: reportUrl || null,
        vendor_remarks: remarks || null,
      };
      const { error } = await getSupabase()
        .from("work_orders")
        .update(payload as never)
        .eq("id", wo.id);
      if (error) throw error;
      toast.success("Vendor service details saved");
      qc.invalidateQueries({ queryKey: ["work_order", wo.id] });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Vendor Service Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Vendor Call ID">
            <Input value={callId} onChange={(e) => setCallId(e.target.value)} disabled={!canEdit} />
          </Field>
          <Field label="Vendor Contacted At">
            <Input
              type="datetime-local"
              value={contactedAt}
              onChange={(e) => setContactedAt(e.target.value)}
              disabled={!canEdit}
            />
          </Field>
          <Field label="Vendor Engineer Name">
            <Input value={engName} onChange={(e) => setEngName(e.target.value)} disabled={!canEdit} />
          </Field>
          <Field label="Vendor Engineer Phone">
            <Input
              type="tel"
              maxLength={10}
              value={engPhone}
              onChange={(e) => setEngPhone(stripPhone(e.target.value).slice(0, 10))}
              placeholder="10-digit mobile"
              disabled={!canEdit}
              className={validatePhone(engPhone) ? "border-red-500" : ""}
            />
            {validatePhone(engPhone) && (
              <p className="text-[11px] text-red-500 mt-0.5">{validatePhone(engPhone)}</p>
            )}
          </Field>
          <Field label="Vendor Visit Date/Time">
            <Input
              type="datetime-local"
              value={visitAt}
              onChange={(e) => setVisitAt(e.target.value)}
              disabled={!canEdit}
            />
          </Field>
          <Field label="Service Report URL">
            <Input
              value={reportUrl}
              onChange={(e) => setReportUrl(e.target.value)}
              placeholder="https://…"
              disabled={!canEdit}
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="Vendor Remarks">
              <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={!canEdit} />
            </Field>
          </div>
        </div>
        {canEdit && (
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Vendor Details"}
          </Button>
        )}
        {!canEdit && <p className="text-xs text-muted-foreground">Read-only for your role.</p>}
      </CardContent>
    </Card>
  );
}

type SlaResult = { state: "met" | "breached" | "pending" | "na"; detail: string };

export function computeSlaState(reportedAt: string | null, endAt: string | null, slaHours: number | null): SlaResult {
  if (!reportedAt || slaHours == null) return { state: "na", detail: "" };
  const reportedMs = new Date(reportedAt).getTime();
  const slaMs = slaHours * 3_600_000;
  const dueMs = reportedMs + slaMs;
  if (endAt) {
    const endMs = new Date(endAt).getTime();
    if (endMs <= dueMs) return { state: "met", detail: "" };
    const overHours = Math.max(1, Math.round((endMs - dueMs) / 3_600_000));
    return { state: "breached", detail: `by ${overHours}h` };
  }
  const nowMs = Date.now();
  if (nowMs <= dueMs) return { state: "pending", detail: "" };
  const overHours = Math.max(1, Math.round((nowMs - dueMs) / 3_600_000));
  return { state: "breached", detail: `by ${overHours}h` };
}

function SlaPill({ label, state, detail }: { label: string; state: SlaResult["state"]; detail: string }) {
  if (state === "na") {
    return (
      <Badge variant="outline" className="text-muted-foreground">
        {label}: Not configured
      </Badge>
    );
  }
  if (state === "met") {
    return (
      <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 dark:text-emerald-300">
        {label}: Met
      </Badge>
    );
  }
  if (state === "pending") {
    return (
      <Badge variant="outline" className="border-amber-500/40 text-amber-700 dark:text-amber-300">
        {label}: Pending
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-destructive/40 text-destructive">
      {label}: Breached{detail ? ` ${detail}` : ""}
    </Badge>
  );
}

function RelatedRecordsCard({ assetId, woId }: { assetId?: string | null; woId: string }) {
  const { data: priorWos } = useQuery({
    queryKey: ["prior-wos", assetId],
    enabled: !!assetId,
    queryFn: async () => {
      const res = await apiGet<{ results: { id: string; wo_number: string; status: string; reported_at: string }[] }>(
        `/api/v1/work-orders/?asset_id=${assetId}&limit=6`
      );
      return res.results.filter((w) => w.id !== woId).slice(0, 5);
    }
  });

  const { data: condemnationReqs } = useQuery({
    queryKey: ["related-condemnation", assetId],
    enabled: !!assetId,
    queryFn: async () => {
      const res = await apiGet<{ results: { id: string; request_no: string; status: string }[] }>(
        `/api/v1/condemnation/condemnationrequest/?asset_id=${assetId}`
      );
      return res.results;
    }
  });

  if (!priorWos?.length && !condemnationReqs?.length) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Related Records</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {priorWos && priorWos.length > 0 && (
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-2">Prior Work Orders (Asset)</div>
            <div className="space-y-2">
              {priorWos.map((w) => (
                <div key={w.id} className="flex items-center justify-between text-sm">
                  <Link to="/work-orders/$woId" params={{ woId: w.id }} className="font-mono text-primary hover:underline">
                    {w.wo_number}
                  </Link>
                  <span className="text-xs text-muted-foreground">{formatDate(w.reported_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {condemnationReqs && condemnationReqs.length > 0 && (
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-2">Condemnation Record</div>
            <div className="space-y-2">
              {condemnationReqs.map((req) => (
                <div key={req.id} className="flex items-center justify-between text-sm">
                  <Link to="/condemnation/$id" params={{ id: req.id }} className="font-mono text-primary hover:underline">
                    {req.request_no || "View Request"}
                  </Link>
                  <Badge variant="outline" className="text-[10px]">{req.status}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Placeholders for Procurement Records which require backend implementations later */}
        <div className="text-xs text-muted-foreground pt-2 border-t border-dashed">
          Linked Indent / RFQ / PO (Not yet available)
        </div>
      </CardContent>
    </Card>
  );
}

